import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { functions, auth } from "../firebaseConfig";
import { httpsCallable } from "firebase/functions";
import { signInWithEmailAndPassword } from "firebase/auth";
import { useLanguage } from "../contexts/LanguageContext";
import { useSystemSettings } from "../hooks/usesystemsettings";

const RegisterPage = () => {
  const { t, isRTL } = useLanguage();
  // System admin can disable public signup from /admin. When disabled,
  // we redirect silently to /login (per product requirement) — no
  // banner, no error, just send the user to the login screen. The
  // backend enforces this too in createTenant; the redirect is the
  // user-facing equivalent.
  const { settings, loading: settingsLoading } = useSystemSettings();
  const navigate = useNavigate();

  useEffect(() => {
    if (!settingsLoading && settings.signupEnabled === false) {
      navigate("/login", { replace: true });
    }
  }, [settingsLoading, settings.signupEnabled, navigate]);

  const [companyName, setCompanyName] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerPassword, setOwnerPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [businessType, setBusinessType] = useState("agency");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault(); setError("");
    if (!companyName || !ownerName || !ownerEmail || !ownerPassword || !businessType) { setError(t("no_data")); return; }
    if (ownerPassword !== confirmPassword) { setError(t("passwords_no_match")); return; }
    if (ownerPassword.length < 6) { setError(t("min_password")); return; }
    setLoading(true);
    try {
      const createTenant = httpsCallable(functions, "createTenant");
      await createTenant({ companyName, ownerName, ownerEmail, ownerPassword, businessType });
      await signInWithEmailAndPassword(auth, ownerEmail, ownerPassword);
      navigate("/company");
    } catch (err) {
      // failed-precondition = signup gate triggered between page load
      // and submit (admin toggled it off mid-form). Send the user to
      // login silently to match the route-level behaviour above.
      if (err.code === "functions/failed-precondition") {
        navigate("/login", { replace: true });
        return;
      }
      setError(err.code === "functions/already-exists" ? t("login_invalid") : err.message || t("register_failed"));
    } finally { setLoading(false); }
  };

  // While we're checking the flag, render nothing — avoids flashing
  // the signup form for a frame before the redirect kicks in.
  if (settingsLoading || settings.signupEnabled === false) return null;

  const businessTypes = [
    { value: "agency", label: t("business_type_agency"), hint: t("business_type_hint_agency") },
    { value: "store", label: t("business_type_store"), hint: t("business_type_hint_store") },
    { value: "service", label: t("business_type_service"), hint: t("business_type_hint_service") },
  ];

  const field = (label, input) => (
    <div><label className="block text-gray-300 text-sm font-medium mb-2">{label}</label>{input}</div>
  );
  const inputCls = "w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 transition-colors";

  return (
    <div  className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-4">
      <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-2xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">NexusFlow</h1>
          <p className="text-gray-400">{t("register_company")}</p>
        </div>

        {error && <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-xl mb-6">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-5">
          {field(t("company_name"), <input type="text" value={companyName} onChange={e => setCompanyName(e.target.value)} className={inputCls} placeholder={t("company_name")} required />)}

          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">{t("business_type")}</label>
            <select value={businessType} onChange={e => setBusinessType(e.target.value)} className={inputCls} required>
              {businessTypes.map(bt => <option key={bt.value} value={bt.value}>{bt.label}</option>)}
            </select>
            <p className="text-gray-400 text-xs mt-1">{businessTypes.find(b => b.value === businessType)?.hint}</p>
          </div>

          {field(t("your_name"), <input type="text" value={ownerName} onChange={e => setOwnerName(e.target.value)} className={inputCls} placeholder={t("your_name")} required />)}
          {field(t("email"), <input type="email" value={ownerEmail} onChange={e => setOwnerEmail(e.target.value)} className={inputCls} placeholder={t("email")} required />)}
          {field(t("password"), <input type="password" value={ownerPassword} onChange={e => setOwnerPassword(e.target.value)} className={inputCls} placeholder={`${t("password")} (${t("min_password")})`} required />)}
          {field(t("confirm_password"), <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className={inputCls} placeholder={t("confirm_password")} required />)}

          <button type="submit" disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white font-semibold py-3 rounded-xl transition-colors">
            {loading ? t("creating_account") : t("register")}
          </button>
        </form>

        <p className="text-center text-gray-400 mt-6">
          {t("already_have_account")}{" "}
          <Link to="/login" className="text-blue-400 hover:text-blue-300">{t("sign_in")}</Link>
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;